import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'

interface ServerStatusContextType {
  serverDown: boolean
}

const ServerStatusContext = createContext<ServerStatusContextType>({ serverDown: false })

export function useServerStatus(): ServerStatusContextType {
  return useContext(ServerStatusContext)
}

export function ServerStatusProvider({ children }: { children: ReactNode }) {
  const [serverDown, setServerDown] = useState(false)

  const checkServer = useCallback(() => {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 3000)

    fetch('/api/ping', { signal: controller.signal })
      .then(res => setServerDown(!res.ok))
      .catch(() => setServerDown(true))
      .finally(() => clearTimeout(timeout))
  }, [])

  useEffect(() => {
    // Check immediately on mount
    checkServer()

    // Then poll every 30 seconds
    const interval = setInterval(() => {
      // Skip poll if tab is hidden (save unnecessary fetches)
      if (!document.hidden) {
        checkServer()
      }
    }, 30_000)

    return () => clearInterval(interval)
  }, [checkServer])

  return (
    <ServerStatusContext.Provider value={{ serverDown }}>
      {children}
    </ServerStatusContext.Provider>
  )
}
